import { SearchOutlined } from '@ant-design/icons';
import { Col, DatePicker, Form, FormInstance, Select } from 'antd';
import { AxiosError } from 'axios';
import { useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import GetProducers from '~/@core/application/useCases/rfidReports/inventory/getProducers';
import GetAreas from '~/@core/application/useCases/rfidReports/receipt/getAreas';
import GetNonConformities from '~/@core/application/useCases/rfidReports/receipt/getNonConformities';
import DefaultPaginated from '~/@core/domain/@shared/DefaultPaginated';
import Area from '~/@core/domain/model/rfidReports/Area';
import NonConformity from '~/@core/domain/model/rfidReports/NonConformity';
import Producer from '~/@core/domain/model/rfidReports/Producer';
import FormFilterDataModel, {
  MapFormToFilter
} from '~/@core/domain/ui/forms/FormFilterDataModel';
import DefaultRow from '~/components/bosons/DefaultRow';
import SearchButton from '~/components/Buttons/SearchButton';
import { globalContext } from '~/context/GlobalContext';
import useNonConformityStore from '~/zustand/rfidReports/inventory/nonConformity-state';
import useProducerStore from '~/zustand/rfidReports/inventory/producer-state';
import useAreaStore from '~/zustand/rfidReports/receipt/area-state';
import { SearchButtonDiv } from '../../Inventory/Dones/FormFilterInventoryDone.styled';
import { PermissionNames } from '~/constants/permissionNames';

const { Option } = Select;

interface Props {
  form: FormInstance<FormFilterDataModel>;
}
export default function FormFilterReceiptNonConformity(props: Props) {
  const { form } = props;
  const { t } = useTranslation();
  const {
    nonConformityPagination,
    addNonConformities,
    handleNonConformityPagination,
    setIsLoadingNonConformity
  } = useNonConformityStore();

  const { handlePromiseUseCase, activeAlert } = useContext(globalContext);

  useEffect(() => {
    form.setFieldsValue({
      dtaFilter: []
    });
  }, [form]);

  const { producers, addProducers } = useProducerStore();

  const { areaPagination, addAreas, handleAreaPagination } = useAreaStore();

  useEffect(() => {
    const permissionName = PermissionNames.RfidReports.MenuReceipt;
    const getProducers = new GetProducers(permissionName);

    handlePromiseUseCase<Producer[], AxiosError>(
      getProducers.execute(),
      producerOut => {
        addProducers(producerOut);
      },
      error => {
        activeAlert({
          message: JSON.stringify(
            error.response?.data
              ? error.response?.data
              : 'Houve um erro inesperado!'
          ),
          type: 'error',
          timeout: 5000
        });
      }
    );
  }, []);

  const onSubmitFilterReceiptNonConformity = useCallback(
    (formValues: FormFilterDataModel) => {
      const filterNonConformity = MapFormToFilter.toFilter(formValues, {
        pageSize: nonConformityPagination.pageSize,
        pageNumber: nonConformityPagination.current
      });

      const getNonConformities = new GetNonConformities();
      setIsLoadingNonConformity();

      handlePromiseUseCase<DefaultPaginated<NonConformity[]>, AxiosError>(
        getNonConformities.execute(filterNonConformity),
        nonConformitiesOut => {
          addNonConformities(nonConformitiesOut.results);
          handleNonConformityPagination({
            ...nonConformityPagination,
            total: nonConformitiesOut.totalCount
          });
          setIsLoadingNonConformity();
        },
        error => {
          activeAlert({
            message: JSON.stringify(
              error.response?.data
                ? error.response?.data
                : 'Houve um erro inesperado!'
            ),
            type: 'error',
            timeout: 5000
          });
          setIsLoadingNonConformity();
        }
      );

      const getAreas = new GetAreas();

      handlePromiseUseCase<DefaultPaginated<Area[]>, AxiosError>(
        getAreas.execute(filterNonConformity),
        areaOut => {
          addAreas(areaOut.results);
          handleAreaPagination({
            ...areaPagination,
            total: areaOut.totalCount
          });
        },
        error => {
          activeAlert({
            message: JSON.stringify(
              error.response?.data
                ? error.response?.data
                : 'Houve um erro inesperado!'
            ),
            type: 'error',
            timeout: 5000
          });
        }
      );
    },
    [
      activeAlert,
      addAreas,
      addNonConformities,
      areaPagination,
      handleAreaPagination,
      handleNonConformityPagination,
      handlePromiseUseCase,
      nonConformityPagination,
      setIsLoadingNonConformity
    ]
  );

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={onSubmitFilterReceiptNonConformity}
    >
      <DefaultRow>
        <Col lg={6}>
          <Form.Item name="dtaFilter" label="Data de Consulta: ">
            <DatePicker.RangePicker
              format="DD/MM/YYYY"
              placeholder={['Início', 'Fim']}
            />
          </Form.Item>
        </Col>
        <Col lg={6}>
          <Form.Item
            name="producerFilter"
            label="Sitio: "
            rules={[{ required: true }]}
          >
            <Select allowClear placeholder="Selecione">
              {producers.map(producer => (
                <Option value={producer.account}>
                  {producer.account} - {producer.tradingName}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col lg={3}>
          <SearchButtonDiv>
            <SearchButton
              htmlType="submit"
              type="primary"
              icon={<SearchOutlined />}
            />
          </SearchButtonDiv>
        </Col>
      </DefaultRow>
    </Form>
  );
}
